import {
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  View,
  Text,
  TextInput,
  Image,
  Pressable,
  Platform,
  ScrollView,
  useWindowDimensions,
} from "react-native";
import { Send, CornerDownRight, Info, X } from "lucide-react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  interpolate,
  FadeIn,
  LinearTransition,
} from "react-native-reanimated";
import { useAiChat } from "../hooks/useAiChat";
import type { AiChatResponse, SdkReasoningBlock } from "../hooks/useAiChat";
import type { ContentBlock } from "@src/packages/content";
import type { MainTabParamList } from "../types";
import { getBlock } from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../onboarding/theme";
import { THIINGS } from "../../shared/icons";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import InfoSheet, { InfoButton } from "./InfoSheet";
import SupplementDetailSheet from "./SupplementDetailSheet";
import TestDetailSheet from "./TestDetailSheet";
import ProgramDetailSheet from "./ProgramDetailSheet";
import ProcedureDetailSheet from "./ProcedureDetailSheet";
import {
  AiChatResponseView,
  ReasoningActivityView,
  ShimmerThinkingText,
} from "./AiMessageBlocks";
import type { SharedValue } from "react-native-reanimated";
import { GradientOrb } from "@src/components/ui/GradientOrb";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import type { SupportMessage } from "@src/packages/support/hooks/useSupportConversation";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/* ─── Types ─── */

/* ─── Layout constants ─── */

const COLLAPSED_HEIGHT = 120;
const EXPANDED_HEIGHT = 420;
const FULL_CHAT_BOTTOM_INSET = 120;
const CARD_VERTICAL_PADDING = 16;
const COLLAPSED_PILL_MARGIN_TOP = 14;
const CARD_BORDER_INSET = 2;

const INPUT_PILL_STYLE = {
  flexDirection: "row" as const,
  alignItems: "center" as const,
  height: 44,
  borderRadius: 22,
  backgroundColor: "rgba(255,255,255,0.5)",
  borderWidth: 1,
  borderColor: "rgba(255,255,255,0.6)",
  paddingHorizontal: 16,
  gap: 10,
};

/* ─── ThinkingTimeline ─── */

function ThinkingTimeline({ response }: { response: AiChatResponse | null }) {
  const t = useTranslations();
  const reasoningBlock = response?.blocks.find(
    (
      block,
    ): block is Extract<
      AiChatResponse["blocks"][number],
      { type: "reasoning" }
    > => block.type === "reasoning",
  );
  const sdkReasoning = response?.blocks.find(
    (block): block is SdkReasoningBlock => block.type === "sdk_reasoning",
  );

  if (reasoningBlock?.type === "reasoning") {
    return <ReasoningActivityView block={reasoningBlock} />;
  }

  if (sdkReasoning) {
    return (
      <ReasoningActivityView
        block={{
          type: "reasoning",
          title: sdkReasoning.title,
          summary: sdkReasoning.summary,
          categories: [],
          sources: [],
        }}
      />
    );
  }

  return (
    <View
      accessibilityLabel={t("learn.ai_assistant.analysing")}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 16,
        paddingVertical: 6,
      }}
    >
      <ThinkingOrbStatus
        state="working"
        size={20}
        theme="light"
        surface="none"
        accessibilityLabel={t("learn.ai_message_blocks.thinking")}
      />
      <ShimmerThinkingText />
    </View>
  );
}

/* ─── UserBubble ─── */

function UserBubble({ text, quote }: { text: string; quote?: string }) {
  return (
    <View
      testID="ai-chat-user-bubble"
      style={{ alignItems: "flex-end", marginBottom: 12 }}
    >
      <View style={{ maxWidth: "85%", alignItems: "flex-end" }}>
        {/* Quoted reference to the action item this question is about */}
        {quote ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
              marginBottom: 4,
              marginRight: 6,
              maxWidth: "100%",
            }}
          >
            <CornerDownRight
              size={11}
              color={EMBER.colors.warm}
              strokeWidth={2}
            />
            <Text
              numberOfLines={1}
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "600",
                color: EMBER.colors.warm,
                flexShrink: 1,
              }}
            >
              {quote}
            </Text>
          </View>
        ) : null}
        <View
          style={{
            backgroundColor: "rgba(255,255,255,0.7)",
            borderRadius: 18,
            borderBottomRightRadius: 4,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.8)",
            paddingHorizontal: 14,
            paddingVertical: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.text, lineHeight: 19 }}
          >
            {text}
          </Text>
        </View>
      </View>
    </View>
  );
}

function SupportTeamBubble({
  message,
  label,
}: {
  message: SupportMessage;
  label: string;
}) {
  return (
    <View
      testID="support-team-bubble"
      style={{ alignItems: "flex-start", marginBottom: 12 }}
    >
      <View style={{ maxWidth: "85%", alignItems: "flex-start" }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "600",
            color: EMBER.colors.textFaint,
            marginBottom: 4,
            paddingHorizontal: 3,
          }}
        >
          {label}
        </Text>
        <View
          style={{
            backgroundColor: "rgba(255,255,255,0.7)",
            borderRadius: 18,
            borderBottomLeftRadius: 4,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.8)",
            paddingHorizontal: 14,
            paddingVertical: 10,
          }}
        >
          {message.content ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.text,
                lineHeight: 19,
              }}
            >
              {message.content}
            </Text>
          ) : null}
          {message.attachments.map((attachment) => (
            <Text
              key={attachment.id}
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                fontSize: 12,
                color: EMBER.colors.textLight,
                lineHeight: 17,
                marginTop: message.content ? 6 : 0,
              }}
            >
              {attachment.name ?? "Attachment"}
            </Text>
          ))}
        </View>
      </View>
    </View>
  );
}

/* ─── AiAssistantCard ─── */

type CardProps = {
  expanded: boolean;
  onToggle: () => void;
  progress: SharedValue<number>;
  onRequestScrollTo?: () => void;
  onSend?: (text: string) => void;
  variant?: "card" | "screen";
  title?: string;
  subtitle?: string;
  emptyPrompt?: string;
  emptyContent?: ReactNode;
  supportMessages?: SupportMessage[];
  supportTeamLabel?: string;
  supportLoading?: boolean;
  supportError?: string | null;
  inputPlaceholder?: string;
  inputDisabled?: boolean;
  initialInputText?: string;
  initialAutoSend?: boolean;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  headerClose?: ReactNode;
  headerBottom?: ReactNode;
  showTitle?: boolean;
  showSubtitle?: boolean;
  showEmptyOrb?: boolean;
  showTopOrb?: boolean;
  returnTab?: keyof MainTabParamList;
};

export function AiAssistantCard({
  expanded,
  onToggle,
  progress,
  onRequestScrollTo,
  onSend,
  variant = "card",
  title,
  subtitle,
  emptyPrompt,
  emptyContent,
  supportMessages,
  supportTeamLabel,
  supportLoading = false,
  supportError,
  inputPlaceholder,
  inputDisabled = false,
  initialInputText,
  initialAutoSend = false,
  headerLeft,
  headerRight,
  headerClose,
  headerBottom,
  showTitle = true,
  showSubtitle = true,
  showEmptyOrb = true,
  showTopOrb = false,
  returnTab,
}: CardProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("AiAssistantCard");
  const { height: windowHeight } = useWindowDimensions();
  const resolvedTitle = title ?? t("learn.ai_assistant.default_title");
  const resolvedSubtitle = subtitle ?? t("learn.ai_assistant.default_subtitle");
  const resolvedEmptyPrompt =
    emptyPrompt ?? t("learn.ai_assistant.default_empty_prompt");
  const resolvedInputPlaceholder =
    inputPlaceholder ?? t("learn.ai_assistant.default_input_placeholder");
  const aiInfoSections = [
    {
      title: t("learn.ai_assistant.info_what_title"),
      body: t("learn.ai_assistant.info_what_body"),
    },
    {
      title: t("learn.ai_assistant.info_personalized_title"),
      body: t("learn.ai_assistant.info_personalized_body"),
    },
    {
      title: t("learn.ai_assistant.info_not_advice_title"),
      body: t("learn.ai_assistant.info_not_advice_body"),
    },
  ];
  const { messages, sendMessage, isLoading } = useAiChat();
  const isSupportConversation = supportMessages !== undefined;
  const messageCount = isSupportConversation
    ? supportMessages.length
    : messages.length;
  const [showInfo, setShowInfo] = useState(false);
  const [inputText, setInputText] = useState("");
  const [activeEntity, setActiveEntity] = useState<ContentBlock | null>(null);
  const inputRef = useRef<TextInput>(null);
  const chatScrollRef = useRef<ScrollView>(null);
  const didApplyInitialInput = useRef(false);
  const [collapsedHeaderHeight, setCollapsedHeaderHeight] = useState(28);
  const [collapsedHeaderBottomHeight, setCollapsedHeaderBottomHeight] =
    useState(0);

  // The collapsed card used to assume a one-line subtitle. On narrow screens
  // the subtitle wraps, so the fixed 120px container clipped the input pill.
  // Measure the variable content and keep the original height as a minimum.
  const collapsedHeight = Math.max(
    COLLAPSED_HEIGHT,
    CARD_VERTICAL_PADDING * 2 +
      collapsedHeaderHeight +
      (headerBottom ? 12 + collapsedHeaderBottomHeight : 0) +
      COLLAPSED_PILL_MARGIN_TOP +
      INPUT_PILL_STYLE.height +
      CARD_BORDER_INSET,
  );

  const targetHeight = useSharedValue(EXPANDED_HEIGHT);

  useEffect(() => {
    const hasMessages = messageCount > 0;
    targetHeight.value = withTiming(
      hasMessages
        ? Math.max(windowHeight - FULL_CHAT_BOTTOM_INSET, EXPANDED_HEIGHT)
        : EXPANDED_HEIGHT,
      { duration: hasMessages ? 420 : 350 },
    );
    if (hasMessages) {
      // Keep the newly-morphed conversation anchored to the top of the
      // product route instead of leaving the user at the old card position.
      onRequestScrollTo?.();
    }
  }, [messageCount, onRequestScrollTo, targetHeight, windowHeight]);

  const containerStyle = useAnimatedStyle(() =>
    variant === "screen"
      ? { flex: 1 }
      : {
          height: interpolate(
            progress.value,
            [0, 1],
            [collapsedHeight, targetHeight.value],
          ),
        },
  );

  const pillStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 0.3], [1, 0]),
  }));

  const chatStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0.5, 1], [0, 1]),
  }));

  useEffect(() => {
    if (expanded) {
      const timer = setTimeout(() => inputRef.current?.focus(), 150);
      onRequestScrollTo?.();
      return () => clearTimeout(timer);
    } else {
      inputRef.current?.blur();
    }
  }, [expanded, onRequestScrollTo]);

  const handleBlur = useCallback(() => {
    const hasActiveContent = messages.some(
      (m) => m.role === "assistant" && (m.thinking || m.response),
    );
    if (
      variant !== "screen" &&
      expanded &&
      inputText.trim().length === 0 &&
      messages.length === 0 &&
      !hasActiveContent
    ) {
      onToggle();
    }
  }, [expanded, inputText, messages, onToggle, variant]);

  const scrollToBottom = useCallback(() => {
    setTimeout(
      () => chatScrollRef.current?.scrollToEnd({ animated: true }),
      50,
    );
  }, []);

  const handleOpenEntity = useCallback((kind: string, blockId: string) => {
    const block = getBlock(blockId as Parameters<typeof getBlock>[0]);
    if (block) setActiveEntity(block as ContentBlock);
  }, []);

  const sendText = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!text || isLoading || inputDisabled) return;

      if (onSend) {
        setInputText("");
        onSend(text);
        return;
      }

      setInputText("");
      scrollToBottom();

      await sendMessage(text);

      scrollToBottom();
    },
    [inputDisabled, isLoading, onSend, scrollToBottom, sendMessage],
  );

  const handleSend = useCallback(async () => {
    // Both the send button and the keyboard return key funnel through here,
    // and neither carries visible copy of its own.
    capturePress("send_message");
    await sendText(inputText);
  }, [capturePress, inputText, sendText]);

  useEffect(() => {
    if (!initialInputText || didApplyInitialInput.current) return;
    didApplyInitialInput.current = true;

    if (initialAutoSend) {
      setInputText("");
      void sendText(initialInputText);
      return;
    }

    setInputText(initialInputText);
  }, [initialAutoSend, initialInputText, sendText]);

  const hasMessages = messageCount > 0;
  const showTopOrbRow = showTopOrb && hasMessages;
  // Care and Support are not backed by the AI conversation contract. Keep the
  // shared provider history from appearing in either mode while preserving it
  // for the AI mode when the patient switches back.
  const showConversation = isSupportConversation
    ? hasMessages
    : hasMessages && !inputDisabled && !onSend;
  const orbIsActive =
    showTopOrbRow &&
    (isLoading ||
      supportLoading ||
      messages.some(
        (message) => message.role === "assistant" && message.thinking === true,
      ));
  const orbScale = useSharedValue(1);

  useEffect(() => {
    orbScale.value = withTiming(orbIsActive ? 1.45 : 1, { duration: 280 });
  }, [orbIsActive, orbScale]);

  const orbAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: orbScale.value }],
  }));

  return (
    <View
      testID="ai-assistant-card"
      style={variant === "screen" ? { flex: 1 } : { marginTop: 20 }}
    >
      <Animated.View
        style={[
          {
            ...(GLASS_CARD_STYLE as object),
            ...(variant === "screen" && {
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              borderRadius: 24,
            }),
            padding: CARD_VERTICAL_PADDING,
            overflow: "hidden",
          },
          variant === "screen" ? { flex: 1 } : null,
          containerStyle,
        ]}
      >
        {showTopOrb ? (
          <View
            style={{
              alignItems: "center",
              height: 32,
              justifyContent: "center",
              marginBottom: 8,
              position: "relative",
            }}
          >
            <View
              style={{
                position: "absolute",
                left: 0,
                top: 0,
              }}
            >
              {headerRight}
            </View>
            {showTopOrbRow ? (
              <Animated.View
                pointerEvents="none"
                style={[
                  {
                    width: 32,
                    height: 32,
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  orbAnimatedStyle,
                ]}
              >
                <GradientOrb size={32} />
              </Animated.View>
            ) : null}
          </View>
        ) : null}
        {showTopOrb ? (
          <View
            style={{
              position: "absolute",
              top: 16,
              right: 52,
              zIndex: 2,
            }}
          >
            <Pressable
              onPress={() => {
                capturePress("ai_assistant_info");
                setShowInfo(true);
              }}
              hitSlop={10}
              style={{
                width: 32,
                height: 32,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 16,
                backgroundColor: "rgba(255, 255, 255, 0.25)",
              }}
            >
              <Info
                size={14}
                color={EMBER.colors.textFaint}
                strokeWidth={1.8}
              />
            </Pressable>
          </View>
        ) : null}

        {/* Header */}
        <View
          onLayout={(event) => {
            const nextHeight = Math.ceil(event.nativeEvent.layout.height);
            setCollapsedHeaderHeight((currentHeight) =>
              currentHeight === nextHeight ? currentHeight : nextHeight,
            );
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <View
            style={{
              flex: 1,
              minWidth: 0,
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
            }}
          >
            {headerLeft !== undefined ? (
              headerLeft
            ) : (
              <Image
                source={{ uri: THIINGS.thoughtBubble }}
                style={{ width: 28, height: 28 }}
              />
            )}
            <View style={{ flex: 1, minWidth: 0 }}>
              {showTitle ? (
                <Text
                  className="font-dm-sans"
                  numberOfLines={2}
                  style={{
                    fontSize: 15,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  {resolvedTitle}
                </Text>
              ) : null}
              {showSubtitle ? (
                <Text
                  className="font-dm-sans"
                  numberOfLines={2}
                  style={{
                    fontSize: 12,
                    color: EMBER.colors.textLight,
                    marginTop: showTitle ? 1 : 0,
                  }}
                >
                  {resolvedSubtitle}
                </Text>
              ) : null}
            </View>
          </View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            {!showTopOrb ? headerRight : null}
            {!showTopOrb ? (
              <InfoButton onPress={() => setShowInfo(true)} />
            ) : null}
            {expanded && variant !== "screen" && (
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("chat.hub.close_chat")),
                    "chat.hub.close_chat",
                  );
                  onToggle();
                }}
                hitSlop={12}
                accessibilityRole="button"
                accessibilityLabel={t("chat.hub.close_chat")}
                testID="ai-chat-close"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.55)",
                  borderWidth: 1,
                  borderColor: "rgba(255,255,255,0.65)",
                }}
              >
                <X size={16} color={EMBER.colors.textMid} strokeWidth={2} />
              </Pressable>
            )}
          </View>
        </View>
        {headerClose ? (
          <View
            style={{
              position: "absolute",
              top: 16,
              right: 12,
              zIndex: 2,
            }}
          >
            {headerClose}
          </View>
        ) : null}
        {headerBottom ? (
          <View
            onLayout={(event) => {
              const nextHeight = Math.ceil(event.nativeEvent.layout.height);
              setCollapsedHeaderBottomHeight((currentHeight) =>
                currentHeight === nextHeight ? currentHeight : nextHeight,
              );
            }}
            style={{ marginTop: 12, alignItems: "flex-end" }}
          >
            {headerBottom}
          </View>
        ) : null}

        {/* Collapsed: tappable pill */}
        {!expanded && (
          <Animated.View style={[{ marginTop: 14 }, pillStyle]}>
            <Pressable
              onPress={() => {
                capturePress(pressLabel(resolvedInputPlaceholder), "open_chat");
                onToggle();
              }}
              accessibilityRole="button"
              accessibilityLabel={resolvedInputPlaceholder}
              style={INPUT_PILL_STYLE}
            >
              <Text
                className="font-dm-sans"
                style={{ fontSize: 14, color: EMBER.colors.textFaint, flex: 1 }}
              >
                {resolvedInputPlaceholder}
              </Text>
              <Send
                size={16}
                color={EMBER.colors.textFaint}
                strokeWidth={1.5}
              />
            </Pressable>
          </Animated.View>
        )}

        {/* Expanded: chat + input */}
        {expanded && (
          <Animated.View style={[{ flex: 1, marginTop: 16 }, chatStyle]}>
            <ScrollView
              ref={chatScrollRef}
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
              keyboardDismissMode="on-drag"
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{
                flexGrow: 1,
                paddingHorizontal: 4,
                paddingBottom: 4,
                ...(showConversation
                  ? { justifyContent: "flex-start" }
                  : { justifyContent: "center", alignItems: "center" }),
              }}
              style={{ flex: 1 }}
            >
              {showConversation ? (
                isSupportConversation ? (
                  supportMessages.map((message) =>
                    message.direction === "patient" ? (
                      <UserBubble key={message.id} text={message.content} />
                    ) : (
                      <SupportTeamBubble
                        key={message.id}
                        message={message}
                        label={supportTeamLabel ?? "Louise product support"}
                      />
                    ),
                  )
                ) : (
                  messages.map((msg, i) => {
                    if (msg.role === "user") {
                      return (
                        <UserBubble key={i} text={msg.text} quote={msg.quote} />
                      );
                    }

                    return (
                      <Animated.View
                        key={i}
                        entering={
                          msg.thinking ? FadeIn.duration(300) : undefined
                        }
                        layout={LinearTransition.duration(240)}
                        style={{ marginBottom: 8 }}
                      >
                        {msg.thinking ? (
                          <ThinkingTimeline response={msg.response} />
                        ) : msg.response ? (
                          <Animated.View entering={FadeIn.duration(260)}>
                            <AiChatResponseView
                              response={msg.response}
                              onOpenEntity={handleOpenEntity}
                            />
                          </Animated.View>
                        ) : null}
                      </Animated.View>
                    );
                  })
                )
              ) : (
                <View
                  style={{
                    alignItems: "center",
                    gap: 18,
                    maxWidth: 390,
                    paddingHorizontal: 12,
                  }}
                >
                  {variant === "screen" && !emptyContent && showEmptyOrb && (
                    <View
                      pointerEvents="none"
                      style={{
                        width: 92,
                        height: 92,
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 2,
                      }}
                    >
                      <GradientOrb size={82} />
                    </View>
                  )}
                  {emptyContent ??
                    (supportLoading ? (
                      <Text
                        className="font-dm-sans"
                        accessibilityLabel="Loading support conversation"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.textFaint,
                          textAlign: "center",
                          lineHeight: 20,
                        }}
                      >
                        {t("chat.hub.support_loading")}
                      </Text>
                    ) : supportError ? (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.textFaint,
                          textAlign: "center",
                          lineHeight: 20,
                        }}
                      >
                        {supportError}
                      </Text>
                    ) : (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.textFaint,
                          textAlign: "center",
                          lineHeight: 20,
                        }}
                      >
                        {resolvedEmptyPrompt}
                      </Text>
                    ))}
                </View>
              )}
            </ScrollView>

            {/* Input pill */}
            <View style={INPUT_PILL_STYLE}>
              <TextInput
                ref={inputRef}
                placeholder={resolvedInputPlaceholder}
                placeholderTextColor={EMBER.colors.textFaint}
                value={inputText}
                onChangeText={setInputText}
                onBlur={handleBlur}
                onSubmitEditing={handleSend}
                editable={!inputDisabled}
                returnKeyType="send"
                style={[
                  {
                    flex: 1,
                    height: 44,
                    fontSize: 14,
                    color: EMBER.colors.text,
                    padding: 0,
                  },
                  Platform.OS === "web" &&
                    ({ outlineStyle: "none" } satisfies Record<
                      string,
                      unknown
                    >),
                ]}
              />
              <Pressable
                onPress={handleSend}
                hitSlop={8}
                disabled={inputDisabled}
                accessibilityRole="button"
                accessibilityLabel="Send message"
                testID="ai-chat-send"
              >
                <Send
                  size={16}
                  color={
                    !inputDisabled && inputText.trim().length > 0
                      ? EMBER.colors.warm
                      : EMBER.colors.textFaint
                  }
                  strokeWidth={1.5}
                />
              </Pressable>
            </View>
          </Animated.View>
        )}
      </Animated.View>

      <InfoSheet
        visible={showInfo}
        onClose={() => setShowInfo(false)}
        title={t("learn.ai_assistant.info_sheet_title")}
        sections={aiInfoSections}
      />

      {/* Detail sheets — opened via handleOpenEntity or modal registry */}
      {activeEntity?.kind === "supplement" && (
        <SupplementDetailSheet
          item={activeEntity as ContentBlock<"supplement">}
          onClose={() => setActiveEntity(null)}
        />
      )}
      {(activeEntity?.kind === "test" || activeEntity?.kind === "product") && (
        <TestDetailSheet
          item={activeEntity as ContentBlock<"test"> | ContentBlock<"product">}
          onClose={() => setActiveEntity(null)}
          returnTab={returnTab}
        />
      )}
      {activeEntity?.kind === "program" && (
        <ProgramDetailSheet
          item={activeEntity as ContentBlock<"program">}
          onClose={() => setActiveEntity(null)}
        />
      )}
      {activeEntity?.kind === "procedure" && (
        <ProcedureDetailSheet
          item={activeEntity as ContentBlock<"procedure">}
          onClose={() => setActiveEntity(null)}
        />
      )}
    </View>
  );
}
